import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Plus, Search, Star, Edit3, Copy, Trash2, Eye,
  MoreHorizontal, Grid3X3, List, Clock, Users, Globe, Lock, X,
  ChevronLeft, ChevronRight, Folder, FolderPlus, ArrowUpRight, BarChart3,
  Sparkles, TrendingUp, Tag, Inbox, Share2, Pencil, FolderOpen, Download, Home,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useDashboardsList, useDashboardMutations } from '../hooks/useDashboards';
import { useDashboardFolders } from '../../analytics-shared/hooks/useFolders';
import InsightCard from '../../analytics-shared/components/InsightCard';
import CreateFolderModal from '../../analytics-shared/components/CreateFolderModal';
import { VISIBILITY_META as VIS_META, CHART_PALETTE as CHART_COLORS } from '../../analytics-shared/constants';
import CreateDashboardModal from '../components/modals/CreateDashboardModal';
import ShareModal from '../../analytics-shared/components/ShareModal';
import MoveFolderModal from '../../analytics-shared/components/MoveFolderModal';
import { ASSET_DASHBOARD } from '../../analytics-shared/api/sharesApi';

function timeSince(iso) {
  if (!iso) return 'Never';
  const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

const DashboardMiniPreview = ({ widgets = [] }) => {
  const count = Math.min(widgets.length, 6);
  if (count === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LayoutDashboard className="w-10 h-10 text-slate-200" />
      </div>
    );
  }
  const getHeights = (id) => {
    let seed = id ? id.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 0) : 42;
    return Array.from({ length: 5 }, () => {
      seed = (seed * 1664525 + 1013904223) & 0x7fffffff;
      return 20 + (seed % 70);
    });
  };
  return (
    <div className="grid grid-cols-3 grid-rows-2 gap-1.5 h-full p-1">
      {Array.from({ length: count }).map((_, i) => {
        const w = widgets[i];
        const color = CHART_COLORS[i % CHART_COLORS.length];
        const heights = getHeights(w?.id || String(i));
        return (
          <div key={i} className="rounded-lg overflow-hidden flex items-end gap-0.5 p-1.5"
            style={{ background: `${color}14`, border: `1px solid ${color}28` }}>
            {heights.slice(0, 4).map((h, j) => (
              <div key={j} className="flex-1 rounded-sm"
                style={{ height: `${h}%`, backgroundColor: color, opacity: 0.55 + (h / 100) * 0.3 }} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

const DashboardCard = ({ dashboard: d, selected, onSelect, onView, onEdit, onFavorite, onClone, onDelete, onMove, onShare, onDownload, folders }) => {
  const vis = VIS_META[d.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  return (
    <div onClick={() => onSelect(d)}
      className={`relative flex flex-col bg-white rounded-2xl border transition-all duration-200 cursor-pointer group overflow-hidden ${
        selected ? 'border-indigo-400 shadow-lg ring-1 ring-indigo-300/50' : 'border-slate-100 shadow-sm hover:shadow-lg hover:border-indigo-200/70'
      }`}>
      <div className="relative bg-gradient-to-br from-slate-50 to-indigo-50/40 border-b border-slate-100 overflow-hidden cursor-pointer"
        style={{ height: 120 }} onClick={(e) => { e.stopPropagation(); onView(d); }}>
        <DashboardMiniPreview widgets={d.widgets} />
        {d.favorite && <div className="absolute top-2 left-2"><Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /></div>}
        <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/4 transition-colors" />
      </div>

      <div className="px-4 pt-3 pb-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-1 mb-1">
          <h3 className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 truncate leading-snug transition-colors flex-1 min-w-0"
            onClick={(e) => { e.stopPropagation(); onView(d); }}>{d.name}</h3>
          <button onClick={(e) => { e.stopPropagation(); onFavorite(d.id); }} className="flex-shrink-0 p-0.5 -mt-0.5">
            <Star className={`w-4 h-4 transition-colors ${d.favorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
          </button>
        </div>

        {d.description && <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2">{d.description}</p>}

        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${vis.cls}`}>
            <VisIcon className="w-2.5 h-2.5" />{vis.label}
          </span>
          {d.folder && d.folder !== 'Unfiled' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
              <Folder className="w-2.5 h-2.5" />{d.folder}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full">
            <BarChart3 className="w-2.5 h-2.5" />{d.widgets?.length || 0} widgets
          </span>
        </div>

        <div className="mt-auto flex items-center text-[10px] text-slate-400 gap-2">
          <span>{d.createdBy}</span><span>·</span>
          <Clock className="w-3 h-3" />
          <span>{timeSince(d.lastViewed || d.updatedAt)}</span>
        </div>

        {/* Always-visible card actions — matches the Reports list. */}
        <div className="mt-3 flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); onView(d); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
            <Eye className="w-3 h-3" /> View
          </button>
          <button onClick={(e) => { e.stopPropagation(); onEdit(d); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
            <Edit3 className="w-3 h-3" /> Edit
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button onClick={(e) => e.stopPropagation()} className="p-1.5 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onShare?.(d)}><Share2 className="w-3.5 h-3.5 mr-2" />Share…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownload?.(d)}><Download className="w-3.5 h-3.5 mr-2" />Download…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onClone(d.id)}><Copy className="w-3.5 h-3.5 mr-2" />Clone</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMove?.(d)}>
                <Folder className="w-3.5 h-3.5 mr-2" />Move…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete(d.id)} className="text-red-600 focus:text-red-600">
                <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const DashboardListRow = ({ dashboard: d, selected, onSelect, onView, onEdit, onFavorite, onClone, onDelete, onMove, onShare, onDownload, folders }) => {
  const vis = VIS_META[d.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  return (
    <div onClick={() => onSelect(d)}
      className={`flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 cursor-pointer group transition-colors ${selected ? 'bg-indigo-50/60' : 'hover:bg-slate-50/80'}`}>
      <button onClick={(e) => { e.stopPropagation(); onFavorite(d.id); }} className="flex-shrink-0">
        <Star className={`w-4 h-4 ${d.favorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
      </button>
      <div className="w-16 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" style={{ height: 36 }}>
        <DashboardMiniPreview widgets={d.widgets} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700 truncate transition-colors">{d.name}</p>
        {d.description && <p className="text-xs text-slate-400 truncate">{d.description}</p>}
      </div>
      <div className="w-28 flex-shrink-0 hidden md:block">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${vis.cls}`}>
          <VisIcon className="w-2.5 h-2.5" />{vis.label}
        </span>
      </div>
      <div className="w-28 flex-shrink-0 hidden md:flex items-center gap-1 text-xs text-slate-500">
        <Folder className="w-3 h-3 text-slate-400" />
        <span className="truncate">{d.folder || 'Unfiled'}</span>
      </div>
      <div className="w-20 flex-shrink-0 text-xs text-slate-500 hidden lg:block">{d.widgets?.length || 0} widgets</div>
      <div className="w-24 flex-shrink-0 text-xs text-slate-500 hidden xl:block">{timeSince(d.lastViewed)}</div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={(e) => { e.stopPropagation(); onView(d); }} className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <Eye className="w-3 h-3" /> View
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(d); }} className="px-2.5 py-1 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
          Edit
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button onClick={(e) => e.stopPropagation()} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onShare?.(d)}><Share2 className="w-3.5 h-3.5 mr-2" />Share…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownload?.(d)}><Download className="w-3.5 h-3.5 mr-2" />Download…</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onClone(d.id)}><Copy className="w-3.5 h-3.5 mr-2" />Clone</DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger><Folder className="w-3.5 h-3.5 mr-2" />Move to Folder</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => onMove(d.id, 'Unfiled')}><Inbox className="w-3.5 h-3.5 mr-2 text-slate-400" />Unfiled</DropdownMenuItem>
                {folders.map((f) => (
                  <DropdownMenuItem key={f} onClick={() => onMove(d.id, f)}><Folder className="w-3.5 h-3.5 mr-2 text-indigo-400" />{f}</DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(d.id)} className="text-red-600 focus:text-red-600">
              <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const PreviewPanel = ({ dashboard: d, onClose, onView, onEdit, onDelete, onClone }) => {
  if (!d) return null;
  const vis = VIS_META[d.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 bg-indigo-100 rounded-lg flex-shrink-0">
            <LayoutDashboard className="w-3.5 h-3.5 text-indigo-600" />
          </div>
          <span className="text-sm font-bold text-slate-800 truncate">Dashboard Details</span>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 pb-2">
          <div className="bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-2xl border border-slate-100 overflow-hidden cursor-pointer hover:border-indigo-200 transition-colors"
            style={{ height: 110 }} onClick={() => onView(d)}>
            <DashboardMiniPreview widgets={d.widgets} />
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h2 className="text-base font-bold text-slate-800 leading-snug flex-1 min-w-0">{d.name}</h2>
            <button onClick={() => onView(d)} className="flex items-center gap-1 text-[10px] text-indigo-600 hover:underline flex-shrink-0">
              <ArrowUpRight className="w-3 h-3" /> Open
            </button>
          </div>
          {d.description && <p className="text-xs text-slate-500 leading-relaxed">{d.description}</p>}
        </div>

        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Folder', value: d.folder || 'Unfiled' },
              { label: 'Visibility', value: d.visibility },
              { label: 'Widgets', value: `${d.widgets?.length || 0} widget${d.widgets?.length !== 1 ? 's' : ''}` },
              { label: 'Created By', value: d.createdBy },
              { label: 'Last Viewed', value: timeSince(d.lastViewed) },
              { label: 'Updated', value: timeSince(d.updatedAt) },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-50 rounded-xl p-2.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                <p className="text-xs font-semibold text-slate-700 mt-0.5 truncate">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full ${vis.cls}`}>
            <VisIcon className="w-3 h-3" />{vis.label}
          </span>
          {d.tags?.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full">
              <Tag className="w-2.5 h-2.5" />{t}
            </span>
          ))}
          {d.favorite && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full">
              <Star className="w-2.5 h-2.5 fill-amber-500" /> Favorited
            </span>
          )}
        </div>

        {d.widgets?.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Widgets ({d.widgets.length})</p>
            <div className="space-y-1">
              {d.widgets.slice(0, 6).map((w, i) => (
                <div key={w.id} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-1.5">
                  <div className="w-4 h-4 rounded flex-shrink-0"
                    style={{ backgroundColor: `${CHART_COLORS[i % CHART_COLORS.length]}40`, border: `1px solid ${CHART_COLORS[i % CHART_COLORS.length]}60` }} />
                  <span className="text-xs text-slate-600 font-medium truncate">{w.title || 'Untitled Widget'}</span>
                  <span className="ml-auto text-[10px] text-slate-400 capitalize flex-shrink-0">{w.chartType}</span>
                </div>
              ))}
              {d.widgets.length > 6 && (
                <p className="text-[10px] text-slate-400 text-center py-1">+{d.widgets.length - 6} more widgets</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pb-4 pt-2 flex flex-col gap-2 flex-shrink-0 border-t border-slate-100">
        <button onClick={() => onView(d)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all shadow-sm">
          <Eye className="w-4 h-4" /> View Dashboard
        </button>
        <div className="flex gap-2">
          <button onClick={() => onEdit(d)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={() => onClone(d.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50">
            <Copy className="w-3.5 h-3.5" /> Clone
          </button>
          <button onClick={() => onDelete(d.id)} className="py-2 px-3 text-xs font-semibold border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const NavItem = ({ icon: Icon, label, count, active, onClick, collapsed }) => (
  <button onClick={onClick} title={collapsed ? label : undefined}
    className={`w-full flex items-center gap-2.5 rounded-xl text-sm transition-all duration-150 mb-0.5 ${
      collapsed ? 'px-2 py-2.5 justify-center' : 'px-3 py-2'
    } ${active ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
    <Icon className={`flex-shrink-0 ${collapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
    {!collapsed && (
      <>
        <span className="flex-1 text-left font-medium truncate">{label}</span>
        {count !== undefined && (
          <span className={`text-xs rounded-full px-1.5 min-w-[20px] text-center ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
        )}
      </>
    )}
  </button>
);

const FolderNavItem = ({ label, count, active, onClick, collapsed, onRename, onDelete }) => (
  <div className={`group/folder flex items-center rounded-xl mb-0.5 ${active ? 'bg-indigo-600' : 'hover:bg-slate-100'}`}>
    <button onClick={onClick} title={collapsed ? label : undefined}
      className={`flex-1 flex items-center gap-2.5 text-sm transition-all duration-150 ${
        collapsed ? 'px-2 py-2.5 justify-center' : 'pl-3 pr-1 py-2'
      } ${active ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`}>
      <Folder className={`flex-shrink-0 ${collapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left font-medium truncate">{label}</span>
          <span className={`text-xs rounded-full px-1.5 min-w-[20px] text-center ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
        </>
      )}
    </button>
    {!collapsed && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button onClick={(e) => e.stopPropagation()}
            className={`opacity-0 group-hover/folder:opacity-100 p-1.5 rounded-lg mr-1 transition-all ${
              active ? 'hover:bg-white/20 text-white' : 'hover:bg-slate-200 text-slate-400'
            }`}>
            <MoreHorizontal className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36">
          <DropdownMenuItem onClick={onRename}><Pencil className="w-3.5 h-3.5 mr-2" />Rename</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600">
            <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )}
  </div>
);

const DashboardListPage = () => {
  const navigate = useNavigate();
  const { dashboards } = useDashboardsList();
  const { folders, create: createFolder, rename: renameFolder, remove: removeFolder } = useDashboardFolders();
  const {
    remove, duplicate, toggleFavorite, markViewed, moveToFolder,
  } = useDashboardMutations();

  const [tab, setTab] = useState('all');
  const [folderFilter, setFolderFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [folderModal, setFolderModal] = useState(null);
  // shareTarget holds the dashboard whose Share modal is currently open.
  // Stored as the full doc (not just an id) so the modal can show name + owner.
  const [shareTarget, setShareTarget] = useState(null);
  // moveTarget: the dashboard whose MoveFolderModal is currently open.
  // Stored as the full doc so the modal can render asset name + the
  // current folder pre-highlighted.
  const [moveTarget, setMoveTarget] = useState(null);

  const counts = useMemo(() => ({
    all: dashboards.length,
    my: dashboards.filter((d) => d.createdBy === 'You').length,
    favorites: dashboards.filter((d) => d.favorite).length,
    team: dashboards.filter((d) => d.visibility === 'Team').length,
    // "Shared" reflects real ACL share records (backend `shared` flag =
    // shared-with-me OR shared-by-me), NOT the visibility string. This is
    // the fix for the "shared 4, only 2 show" count mismatch.
    shared: dashboards.filter((d) => d.shared).length,
    totalWidgets: dashboards.reduce((n, d) => n + (d.widgets?.length || 0), 0),
  }), [dashboards]);

  const folderCounts = useMemo(() => {
    const m = {};
    dashboards.forEach((d) => { if (d.folder) m[d.folder] = (m[d.folder] || 0) + 1; });
    return m;
  }, [dashboards]);

  const filtered = useMemo(() => {
    let list = [...dashboards];
    if (folderFilter) list = list.filter((d) => d.folder === folderFilter);
    else {
      if (tab === 'my') list = list.filter((d) => d.createdBy === 'You');
      else if (tab === 'favorites') list = list.filter((d) => d.favorite);
      else if (tab === 'team') list = list.filter((d) => d.visibility === 'Team');
      else if (tab === 'shared') list = list.filter((d) => d.shared);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q));
    }
    return list;
  }, [dashboards, tab, folderFilter, search]);

  const handleView = useCallback((d) => { markViewed.mutate(d.id); navigate(`/reports/dashboards/${d.id}`); }, [markViewed, navigate]);
  const handleEdit = useCallback((d) => navigate(`/reports/dashboards/${d.id}/edit`), [navigate]);
  const handleFavorite = useCallback((id) => toggleFavorite.mutate(id), [toggleFavorite]);
  const handleClone = useCallback((id) => duplicate.mutate(id).then(() => toast.success('Dashboard cloned')), [duplicate]);
  const handleDelete = useCallback((id) => {
    if (!window.confirm('Delete this dashboard?')) return;
    remove.mutate(id).then(() => {
      if (selectedDashboard?.id === id) setSelectedDashboard(null);
      toast.success('Dashboard deleted');
    });
  }, [remove, selectedDashboard]);
  const handleSelect = useCallback((d) => setSelectedDashboard((prev) => prev?.id === d.id ? null : d), []);
  // Open the searchable MoveFolderModal. The actual mutation runs
  // inside the modal's onMove callback (see below), which calls
  // moveToFolder.mutate and the mutation hook's onSuccess fires
  // invalidateQueries(['dashboards']) — same instant-refresh path
  // the old inline submenu used.
  const handleOpenMove = useCallback((d) => setMoveTarget(d), []);
  const handleShare = useCallback((d) => setShareTarget(d), []);
  // List-page Download → navigate to the viewer with ?download=1.
  // The viewer auto-opens the DashboardExportModal once the
  // widgets have rendered, so the capture isn't a blank canvas.
  const handleDownload = useCallback(
    (d) => navigate(`/reports/dashboards/${d.id}?download=1`),
    [navigate],
  );

  // Async handlers so the shared CreateFolderModal can await the mutation
  // and show its in-flight spinner. They also throw on failure so the
  // modal surfaces the error inline instead of swallowing it.
  const handleFolderCreate = async (name) => {
    await createFolder.mutate(name);
    setFolderModal(null);
    toast.success(`Folder "${name}" created`);
  };
  const handleFolderRename = async (newName) => {
    const oldName = folderModal?.old;
    if (!oldName) return;
    await renameFolder.mutate({ oldName, newName });
    if (folderFilter === oldName) setFolderFilter(newName);
    setFolderModal(null);
    toast.success('Folder renamed');
  };
  const handleFolderDelete = (name) => {
    if (!window.confirm(`Delete folder "${name}"? Dashboards will move to Unfiled.`)) return;
    removeFolder.mutate(name).then(() => {
      if (folderFilter === name) setFolderFilter(null);
      toast.success('Folder deleted');
    });
  };

  return (
    <div className="h-full flex flex-col bg-[#f4f5f8] overflow-hidden">
      <div className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 z-30 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <button onClick={() => setSidebarOpen((v) => !v)} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 flex-shrink-0">
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="p-1.5 bg-indigo-100 rounded-lg"><LayoutDashboard className="w-4 h-4 text-indigo-600" /></div>
          <span className="text-sm font-bold text-slate-800">Dashboards</span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-400">Amroar CRM</span>
        </div>

        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dashboards…"
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-colors" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <button onClick={() => navigate('/crm-platform')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-indigo-600 transition-colors"
            title="Back to CRM">
            <Home className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to CRM</span>
          </button>
          <button onClick={() => navigate('/reports')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
            <BarChart3 className="w-3.5 h-3.5" /> Reports
          </button>
          <button onClick={() => setFolderModal({ mode: 'create' })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
            <FolderPlus className="w-3.5 h-3.5" />
            <span className="hidden md:inline">New Folder</span>
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all shadow-sm shadow-indigo-200">
            <Plus className="w-3.5 h-3.5" /> New Dashboard
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className={`flex-shrink-0 bg-white border-r border-slate-200 flex flex-col transition-all duration-200 overflow-hidden ${sidebarOpen ? 'w-56' : 'w-14'}`}>
          <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-1 pb-1.5">Overview</p>}
            <NavItem icon={LayoutDashboard} label="All Dashboards" count={counts.all}
              active={tab === 'all' && !folderFilter} onClick={() => { setTab('all'); setFolderFilter(null); }} collapsed={!sidebarOpen} />
            <NavItem icon={BarChart3} label="Reports" active={false} onClick={() => navigate('/reports')} collapsed={!sidebarOpen} />

            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-3 pb-1.5">My Dashboards</p>}
            {!sidebarOpen && <div className="h-2" />}
            <NavItem icon={Lock} label="My Dashboards" count={counts.my}
              active={tab === 'my' && !folderFilter} onClick={() => { setTab('my'); setFolderFilter(null); }} collapsed={!sidebarOpen} />
            <NavItem icon={Star} label="Favorites" count={counts.favorites}
              active={tab === 'favorites' && !folderFilter} onClick={() => { setTab('favorites'); setFolderFilter(null); }} collapsed={!sidebarOpen} />
            <NavItem icon={Users} label="Team" count={counts.team}
              active={tab === 'team' && !folderFilter} onClick={() => { setTab('team'); setFolderFilter(null); }} collapsed={!sidebarOpen} />
            <NavItem icon={Share2} label="Shared" count={counts.shared}
              active={tab === 'shared' && !folderFilter} onClick={() => { setTab('shared'); setFolderFilter(null); }} collapsed={!sidebarOpen} />

            {(sidebarOpen || folders.length > 0) && (
              <>
                {sidebarOpen && (
                  <div className="flex items-center justify-between px-3 pt-3 pb-1.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Folders</p>
                    <button onClick={() => setFolderModal({ mode: 'create' })}
                      className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 transition-colors" title="New folder">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {!sidebarOpen && <div className="h-2" />}
                {folders.length === 0 && sidebarOpen && (
                  <div className="px-3 py-2">
                    <p className="text-[10px] text-slate-400 italic">No folders yet</p>
                    <button onClick={() => setFolderModal({ mode: 'create' })}
                      className="mt-1 text-[10px] text-indigo-600 hover:underline font-medium">+ Create folder</button>
                  </div>
                )}
                {folders.map((folder) => (
                  <FolderNavItem key={folder} label={folder}
                    count={folderCounts[folder] || 0}
                    active={folderFilter === folder}
                    onClick={() => setFolderFilter((f) => f === folder ? null : folder)}
                    collapsed={!sidebarOpen}
                    onRename={() => setFolderModal({ mode: 'rename', old: folder })}
                    onDelete={() => handleFolderDelete(folder)} />
                ))}
              </>
            )}

            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-3 pb-1.5">Intelligence</p>}
            {!sidebarOpen && <div className="h-2" />}
            <NavItem icon={Sparkles} label="AI Insights" active={false} onClick={() => {}} collapsed={!sidebarOpen} />
            <NavItem icon={TrendingUp} label="Trends" active={false} onClick={() => {}} collapsed={!sidebarOpen} />
          </div>
        </div>

        <div className="flex flex-1 min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto flex flex-col min-w-0">
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
              <div className="flex gap-3 overflow-x-auto pb-1">
                <InsightCard icon={LayoutDashboard} label="Total Dashboards" value={counts.all} sub={`${counts.my} created by you`} color="indigo" onClick={() => { setTab('all'); setFolderFilter(null); }} />
                <InsightCard icon={Star} label="Favorites" value={counts.favorites} sub="starred" color="amber" onClick={() => { setTab('favorites'); setFolderFilter(null); }} />
                <InsightCard icon={Users} label="Team Dashboards" value={counts.team} sub="team visibility" color="emerald" onClick={() => { setTab('team'); setFolderFilter(null); }} />
                <InsightCard icon={BarChart3} label="Total Widgets" value={counts.totalWidgets} sub="across all dashboards" color="purple" />
                <InsightCard icon={Folder} label="Folders" value={folders.length} sub="organize dashboards" color="blue" onClick={() => setFolderModal({ mode: 'create' })} />
              </div>
            </div>

            <div className="flex-shrink-0 px-5 pb-3 flex items-center gap-3 flex-wrap">
              {folderFilter ? (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Folder:</span>
                  <div className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-xl">
                    <Folder className="w-3 h-3" />
                    {folderFilter}
                    <button onClick={() => setFolderFilter(null)} className="ml-1 hover:bg-white/20 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 overflow-x-auto flex-wrap">
                  {[
                    { id: 'all',       label: 'All Dashboards', count: counts.all },
                    { id: 'my',        label: 'My Dashboards',  count: counts.my },
                    { id: 'favorites', label: 'Favorites',      count: counts.favorites },
                    { id: 'team',      label: 'Team',           count: counts.team },
                    { id: 'shared',    label: 'Shared',         count: counts.shared },
                  ].map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                      className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-xl transition-all ${
                        tab === t.id ? 'bg-slate-800 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      {t.label}
                      <span className={`ml-1.5 ${tab === t.id ? 'opacity-60' : 'text-slate-400'}`}>{t.count}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-slate-400">
                  <span className="font-semibold text-slate-600">{filtered.length}</span> dashboard{filtered.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {[{ mode: 'grid', icon: Grid3X3 }, { mode: 'list', icon: List }].map(({ mode, icon: Icon }) => (
                    <button key={mode} onClick={() => setViewMode(mode)}
                      className={`p-2 transition-colors ${viewMode === mode ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 px-5 pb-6">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center mb-4">
                    {folderFilter
                      ? <FolderOpen className="w-8 h-8 text-indigo-300" />
                      : search
                        ? <Search className="w-8 h-8 text-indigo-300" />
                        : <Inbox className="w-8 h-8 text-indigo-300" />}
                  </div>
                  <h3 className="text-base font-bold text-slate-600">
                    {folderFilter ? `No dashboards in "${folderFilter}"` : search ? `No results for "${search}"` : 'No dashboards yet'}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1 max-w-xs">
                    {folderFilter ? 'Move dashboards here using the ⋯ menu on any card' : search ? 'Try a different search term' : 'Create a dashboard to visualize your reports'}
                  </p>
                  {!search && !folderFilter && (
                    <button onClick={() => setShowCreate(true)}
                      className="mt-5 flex items-center gap-2 px-4 py-2 text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700">
                      <Plus className="w-4 h-4" /> New Dashboard
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filtered.map((d) => (
                    <DashboardCard key={d.id} dashboard={d} selected={selectedDashboard?.id === d.id}
                      onSelect={handleSelect} onView={handleView} onEdit={handleEdit}
                      onFavorite={handleFavorite} onClone={handleClone} onDelete={handleDelete}
                      onMove={handleOpenMove} onShare={handleShare} onDownload={handleDownload} folders={folders} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-4 px-4 py-2.5 border-b border-slate-100 bg-slate-50/70">
                    <div className="w-4" />
                    <div className="w-16 flex-shrink-0" />
                    <div className="flex-1 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Dashboard</div>
                    <div className="w-28 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden md:block">Visibility</div>
                    <div className="w-28 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden md:block">Folder</div>
                    <div className="w-20 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden lg:block">Widgets</div>
                    <div className="w-24 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden xl:block">Last Viewed</div>
                    <div className="w-32 flex-shrink-0" />
                  </div>
                  {filtered.map((d) => (
                    <DashboardListRow key={d.id} dashboard={d} selected={selectedDashboard?.id === d.id}
                      onSelect={handleSelect} onView={handleView} onEdit={handleEdit}
                      onFavorite={handleFavorite} onClone={handleClone} onDelete={handleDelete}
                      onMove={handleOpenMove} onShare={handleShare} onDownload={handleDownload} folders={folders} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={`flex-shrink-0 transition-all duration-250 ease-in-out overflow-hidden ${selectedDashboard ? 'w-72 xl:w-80' : 'w-0'}`}>
            <PreviewPanel
              dashboard={selectedDashboard}
              onClose={() => setSelectedDashboard(null)}
              onView={handleView} onEdit={handleEdit}
              onFavorite={handleFavorite} onClone={handleClone} onDelete={handleDelete} />
          </div>
        </div>
      </div>

      <CreateDashboardModal open={showCreate} onClose={() => setShowCreate(false)} />

      <CreateFolderModal
        open={folderModal !== null}
        mode={folderModal?.mode || 'create'}
        initialName={folderModal?.mode === 'rename' ? folderModal.old : ''}
        existingNames={folders}
        subtitle="Organize and group related dashboards"
        onClose={() => setFolderModal(null)}
        onSave={folderModal?.mode === 'rename' ? handleFolderRename : handleFolderCreate}
      />

      <ShareModal
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        assetType={ASSET_DASHBOARD}
        assetId={shareTarget?.id}
        assetName={shareTarget?.name}
        ownerName={shareTarget?.createdBy}
        ownerId={shareTarget?.createdById}
      />

      <MoveFolderModal
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        folders={folders}
        currentFolder={moveTarget?.folder}
        itemName={moveTarget?.name}
        kindLabel="Dashboard"
        onMove={async (folderName) => {
          if (!moveTarget) return;
          // useDashboardMutations.moveToFolder.onSuccess already calls
          // invalidateQueries(['dashboards']), so the list cache + the
          // folder badge update automatically.
          await moveToFolder.mutate({ id: moveTarget.id, folder: folderName });
          toast.success(`Moved to "${folderName}"`);
        }}
      />
    </div>
  );
};

export default DashboardListPage;
